(ns comms.sente
  (:require [cljs.core.async :refer [<! go-loop]]
            [common.localization :as loc]
            [re-frame.core :as rf]
            [ui.dom-globals :as dg]
            [taoensso.sente :as sente]))

(def transport-failures
  "Sente's own replies when a request never completes.

   They are transport states, not answers: `:chsk/timeout` means the server
   did not reply in the time allowed, `:chsk/closed` that the socket was not
   open to carry the request. Neither says anything about what was being
   asked, which is why they have to be turned into something before anyone
   sees them.

   `:chsk` is Sente's abbreviation for channel socket. Shown raw it tells a
   user nothing and a developer only slightly more."
  {:chsk/timeout :error/request-timed-out
   :chsk/closed  :error/connection-lost})

(defn transport-failure?
  [reply]
  (contains? transport-failures reply))

(defn transport-failure-message
  "A sentence for a failed request, in the user's language. Falls back to a
   generic one rather than to the raw reply, so nothing internal reaches the
   screen by default."
  [reply]
  (loc/tr (get transport-failures reply :error/request-failed)))

(defonce state* (atom nil))
(defonce state-listeners* (atom {}))
(defonce recv-listeners* (atom {}))

(defn framework-event-id?
  [event-id]
  (and (keyword? event-id)
       (= "chsk" (namespace event-id))))

(defn ^:private read-csrf-token []
  (some-> js/document
          (dg/query-selector "meta[name='csrf-token']")
          (dg/get-attribute "content")
          (str)))

(defn make-client
  []
  (let [csrf (read-csrf-token)]
    (js/console.log "MAKE Sente client : " csrf)
    (sente/make-channel-socket-client!
     "/chsk"
     csrf
     {:type           :auto
      :packer         :edn
      ;; Sente >= 1.18 defaults this to false, delivering server pushes with
      ;; their own :id (e.g. :updates/push) straight onto ch-recv. Our receive
      ;; loop's application-event branch is :chsk/recv, which destructures
      ;; ?data as [ev-id ev-data]; without wrapping, every push falls through
      ;; to the default "Unhandled event" case. Enable wrapping so pushes are
      ;; delivered as [:chsk/recv [ev-id ev-data]].
      :wrap-recv-evs? true})))

(defn init! []
  (let [{:keys [chsk ch-recv send-fn state]} (make-client)]
    (reset! state* {:chsk    chsk
                    :ch-chsk ch-recv
                    :send!   send-fn
                    :open?   false
                    :state   state})
    (go-loop []
      (when-let [{:keys [id ?data]} (<! ch-recv)]
        (case id
          ;; The third element is open-changed?, not first-open?. It is true
          ;; on every open, so a listener that read it as first-open? never
          ;; saw a reconnect. Sente puts :first-open? in new-state instead.
          :chsk/state (let [[old-state new-state _open-changed?] ?data]
                        (js/console.log "Sente state changed :" (true? (:open? new-state)) new-state)
                        (swap! state* assoc :open? (true? (:open? new-state)))
                        (doseq [[_ listener] @state-listeners*]
                          (listener {:old-state old-state
                                     :new-state new-state
                                     :first-connect? (:first-open? new-state)})))
          :chsk/recv (let [[ev-id ev-data] ?data
                           handled? (boolean
                                     (some (fn [[_ listener]]
                                             (listener ev-id ev-data))
                                           @recv-listeners*))]
                       (when-not handled?
                         (when-not (framework-event-id? ev-id)
                           (rf/dispatch [ev-id ev-data]))))
          :chsk/handshake nil
          :chsk/ws-ping (constantly nil)
          (js/console.log "Unhandled event" id ?data))
        (recur)))))

(defn add-state-listener!
  [listener-id listener]
  (swap! state-listeners* assoc listener-id listener))

(defn remove-state-listener!
  [listener-id]
  (swap! state-listeners* dissoc listener-id))

(defn add-recv-listener!
  [listener-id listener]
  (swap! recv-listeners* assoc listener-id listener))

(defn remove-recv-listener!
  [listener-id]
  (swap! recv-listeners* dissoc listener-id))

(defn rpc!
  "Send event `ev` and call `on-ok` with reply once received."
  [ev on-ok]
  (js/console.log "SENTE RPC : " ev)

  (if-let [send! (:send! @state*)]
    (send! ev 100000 (fn [resp]
                       (js/console.log "RPC! resp : " resp)
                       (on-ok resp)))
    (js/console.warn "Sente not initialized")))

(defn reconnect!
  "Drop and re-establish the socket so the server re-runs its handshake.

   Sente resolves a connection's uid ONCE, in `user-id-fn` at handshake, and
   never revisits it. The app opens this socket at boot, before the user has
   authenticated, so the server finds no token on the cookie and stamps the
   connection `:sente/nil-uid` - permanently, because a healthy socket never
   reconnects on its own. Every later request over it is therefore
   unauthenticated no matter how the session changes, which is why callers must
   reconnect once a login or a tenant switch has put a token on the cookie."
  []
  (if-let [chsk (:chsk @state*)]
    (do
      (js/console.log "Sente reconnect - re-running handshake for a new uid")
      (sente/chsk-reconnect! chsk))
    (js/console.warn "Sente not initialized - cannot reconnect")))

(defn open?
  []
  (true? (:open? @state*)))

(defn rpc-when-open! [ev on-ok]
  (letfn [(attempt []
            (if (open?)
              (rpc! ev on-ok)
              (do (js/console.log "Wait for socket to open " @state*)
                  (js/setTimeout attempt 1000))))]
    (attempt)))
